import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import ZokuMark from "@/components/ZokuMark";

const links = [
  { label: "Features", href: "#features" },
  { label: "Try it", href: "#try" },
  { label: "Intelligence", href: "#intelligence" },
  { label: "Reviews", href: "#reviews" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-zoku-bg/75 border-b border-stone-200/60" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center leading-none" data-testid="navbar-logo">
          <img
            src={
              scrolled
                ? "https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/22z5px5z__Logo%20%28500%20x%20200%20px%29.png"
                : "https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/d6p51lbc__Logo%20%28500%20x%20200%20px%29%20%281%29.png"
            }
            alt="Zoku"
            className="h-7 md:h-9 w-auto object-contain select-none transition-opacity"
            draggable={false}
            style={{ mixBlendMode: scrolled ? "multiply" : "screen" }}
          />
        </a>

        <ul className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                className={`text-sm transition-colors ${
                  scrolled
                    ? "text-zoku-ink2 hover:text-zoku-ink"
                    : "text-white/90 hover:text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]"
                }`}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://apps.apple.com/in/app/zoku-track-calories/id6762446883"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-0.5 rounded-full px-5 py-2.5 text-sm font-medium transition-all active:scale-95 shadow-sm ${
              scrolled
                ? "bg-zoku-ink text-white hover:bg-stone-800"
                : "bg-white text-zoku-ink hover:bg-stone-100"
            }`}
            data-testid="navbar-download-btn"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/8gdpr0ux_apple-icon-4.png"
              alt=""
              className="w-6 h-6 object-contain -my-2"
              style={scrolled ? { filter: "invert(1) brightness(2)" } : undefined}
            />
            Download app
          </a>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full border transition-colors ${
            scrolled ? "border-stone-200 text-zoku-ink" : "border-white/40 text-white bg-black/20 backdrop-blur"
          }`}
          onClick={() => setOpen(!open)}
          data-testid="navbar-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-zoku-bg/95 backdrop-blur-xl" data-testid="mobile-menu">
          <ul className="flex flex-col px-6 py-4 gap-4">
            {links.map((l) => (
              <li key={l.href}>
                <a href={l.href} onClick={() => setOpen(false)} className="block py-2 text-zoku-ink">
                  {l.label}
                </a>
              </li>
            ))}
            <a
              href="https://apps.apple.com/in/app/zoku-track-calories/id6762446883"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-0.5 bg-zoku-ink text-white rounded-full px-5 py-3 text-center font-medium"
              data-testid="mobile-download-btn"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/8gdpr0ux_apple-icon-4.png"
                alt=""
                className="w-6 h-6 object-contain -my-2"
                style={{ filter: "invert(1) brightness(2)" }}
              />
              Download app
            </a>
          </ul>
        </div>
      )}
    </motion.header>
  );
}
